import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchTranslationSample,
  fetchAllIds,
  updateTranslation,
  triggerTranslation,
  fetchGlossary,
  upsertGlossaryEntry,
  deleteGlossaryEntry,
  TRANSLATION_LANGS,
  translationEditsFromItem,
  langField,
  type RefItem,
  type RefTable,
  type GlossaryEntry,
} from '../lib/referentialService';
import { logger } from '../lib/logger';

const TABLES: { key: RefTable; label: string }[] = [
  { key: 'sectors', label: 'Secteurs' },
  { key: 'subsectors', label: 'Sous-secteurs' },
  { key: 'positions', label: 'Postes' },
  { key: 'skill_groups', label: 'Groupes' },
  { key: 'skills', label: 'Compétences' },
  { key: 'skill_families', label: 'Familles' },
  { key: 'certifications', label: 'Certifications' },
];

const CAMPAIGN_BATCH = 200;

const TranslationReview: React.FC = () => {
  const [tab, setTab] = useState<'review' | 'glossary'>('review');

  // ── Revue ──
  const [table, setTable] = useState<RefTable>('skills');
  const [lang, setLang] = useState<string>('tr');
  const [sample, setSample] = useState<RefItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [retranslatingId, setRetranslatingId] = useState<string | null>(null);

  // ── Campagne ──
  const [campaignRunning, setCampaignRunning] = useState(false);
  const [campaignProgress, setCampaignProgress] = useState('');

  // ── Glossaire ──
  const [glossary, setGlossary] = useState<GlossaryEntry[]>([]);
  const [glossaryLang, setGlossaryLang] = useState<string>('tr');
  const [newTermFr, setNewTermFr] = useState('');
  const [newTerm, setNewTerm] = useState('');
  const [newNote, setNewNote] = useState('');

  const langValue = (item: RefItem): string => langField(item, lang);

  const loadSample = useCallback(async () => {
    setLoading(true);
    try {
      const items = await fetchTranslationSample(table, lang, 30);
      setSample(items);
      setEdits({});
    } catch (err) {
      logger.error('load_translation_sample', 'TranslationReview', { error: String(err) });
      setSample([]);
    } finally {
      setLoading(false);
    }
  }, [table, lang]);

  useEffect(() => {
    if (tab === 'review') loadSample();
  }, [tab, loadSample]);

  const loadGlossary = useCallback(async () => {
    try {
      setGlossary(await fetchGlossary(glossaryLang));
    } catch (err) {
      logger.error('load_glossary', 'TranslationReview', { error: String(err) });
    }
  }, [glossaryLang]);

  useEffect(() => {
    if (tab === 'glossary') loadGlossary();
  }, [tab, loadGlossary]);

  const saveAndLock = async (item: RefItem) => {
    const value = (edits[item.id] ?? langValue(item)).trim();
    if (!value) return;
    setSavingId(item.id);
    try {
      await updateTranslation(table, item.id, lang, value, true);
      setSample((prev) => prev.filter((i) => i.id !== item.id));
    } catch (err) {
      logger.error('save_translation_review', 'TranslationReview', { error: String(err) });
    } finally {
      setSavingId(null);
    }
  };

  const retranslateOne = async (item: RefItem) => {
    setRetranslatingId(item.id);
    try {
      const result = await triggerTranslation(table as any, [item.id], [lang]);
      if (result?.error) {
        alert(`Traduction : ${result.error}`);
      } else {
        const fresh = await fetchTranslationSample(table, lang, 30);
        const updated = fresh.find((i) => i.id === item.id);
        if (updated) {
          setSample((prev) => prev.map((i) => (i.id === item.id ? updated : i)));
        } else {
          // L'item peut ne pas être dans le nouvel échantillon : recharge ciblée impossible, retire la ligne
          setSample((prev) => prev.filter((i) => i.id !== item.id));
        }
        setEdits((prev) => { const n = { ...prev }; delete n[item.id]; return n; });
      }
    } finally {
      setRetranslatingId(null);
    }
  };

  const runCampaign = async () => {
    if (campaignRunning) return;
    const langLabel = TRANSLATION_LANGS.find((l) => l.key === lang)?.label || lang;
    if (!window.confirm(
      `Re-traduire TOUTE la table « ${TABLES.find((t) => t.key === table)?.label} » en ${langLabel} ?\n`
      + 'Les traductions verrouillées sont préservées. Cette opération peut prendre plusieurs minutes.',
    )) return;

    setCampaignRunning(true);
    setCampaignProgress('Récupération des éléments…');
    try {
      const ids = await fetchAllIds(table);
      let done = 0;
      for (let i = 0; i < ids.length; i += CAMPAIGN_BATCH) {
        const chunk = ids.slice(i, i + CAMPAIGN_BATCH);
        setCampaignProgress(`Traduction ${Math.min(i + CAMPAIGN_BATCH, ids.length)}/${ids.length}…`);
        const result = await triggerTranslation(table as any, chunk, [lang]);
        if (result?.error) {
          setCampaignProgress(`Erreur : ${result.error}`);
          logger.error('translation_campaign', 'TranslationReview', { table, lang, error: result.error });
          setCampaignRunning(false);
          return;
        }
        done += result?.translated || 0;
      }
      setCampaignProgress(`Terminé : ${done} éléments re-traduits en ${langLabel}.`);
      logger.functional('translation_campaign_done', 'TranslationReview', { table, lang, translated: done });
      await loadSample();
    } catch (err) {
      setCampaignProgress(`Erreur : ${err instanceof Error ? err.message : err}`);
    } finally {
      setCampaignRunning(false);
    }
  };

  const addGlossaryEntry = async () => {
    if (!newTermFr.trim() || !newTerm.trim()) return;
    try {
      await upsertGlossaryEntry({ termFr: newTermFr, lang: glossaryLang, term: newTerm, note: newNote });
      setNewTermFr('');
      setNewTerm('');
      setNewNote('');
      await loadGlossary();
    } catch (err) {
      alert(`Erreur : ${err instanceof Error ? err.message : err}`);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Revue des traductions</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Vérifiez un échantillon, corrigez et verrouillez — ou lancez une campagne de re-traduction.
          </p>
        </div>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => setTab('review')}
            className={`px-4 py-2 text-sm font-medium ${tab === 'review' ? 'bg-[#00244F] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            Échantillon
          </button>
          <button
            onClick={() => setTab('glossary')}
            className={`px-4 py-2 text-sm font-medium ${tab === 'glossary' ? 'bg-[#00244F] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            Glossaire
          </button>
        </div>
      </div>

      {tab === 'review' && (
        <>
          {/* Filtres */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <select
              value={table}
              onChange={(e) => setTable(e.target.value as RefTable)}
              className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none"
            >
              {TABLES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none"
            >
              {TRANSLATION_LANGS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
            </select>
            <button
              onClick={loadSample}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-[#00244F] border border-[#00244F]/30 rounded-lg hover:bg-[#00244F]/5 disabled:opacity-50"
            >
              Nouvel échantillon
            </button>
            <div className="flex-1" />
            <button
              onClick={runCampaign}
              disabled={campaignRunning}
              className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg disabled:opacity-50"
            >
              {campaignRunning ? 'Campagne en cours…' : `Re-traduire toute la table (${lang.toUpperCase()})`}
            </button>
          </div>
          {campaignProgress && (
            <div className="mb-4 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              {campaignProgress}
            </div>
          )}

          {/* Liste */}
          {loading ? (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">Chargement…</div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                    <th className="px-4 py-2.5 font-medium w-[35%]">Français</th>
                    <th className="px-4 py-2.5 font-medium">Traduction ({lang.toUpperCase()})</th>
                    <th className="px-4 py-2.5 font-medium w-44 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sample.map((item) => {
                    const isLocked = !!item.translationLocked?.[lang];
                    return (
                      <tr key={item.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2.5 text-gray-800">
                          {item.nameFr}
                          {isLocked && <span className="ml-1.5" title="Verrouillée">🔒</span>}
                        </td>
                        <td className="px-4 py-2.5">
                          <input
                            type="text"
                            value={edits[item.id] ?? langValue(item)}
                            onChange={(e) => setEdits((prev) => ({ ...prev, [item.id]: e.target.value }))}
                            dir={lang === 'ar' ? 'rtl' : 'ltr'}
                            className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                          />
                        </td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          <button
                            onClick={() => saveAndLock(item)}
                            disabled={savingId === item.id}
                            title="Valider + verrouiller"
                            className="px-2.5 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg mr-1.5 disabled:opacity-50"
                          >
                            {savingId === item.id ? '…' : '✓ Valider'}
                          </button>
                          <button
                            onClick={() => retranslateOne(item)}
                            disabled={retranslatingId === item.id || isLocked}
                            title={isLocked ? 'Verrouillée — déverrouillez d\'abord dans Référentiels' : 'Re-traduire avec le nouveau prompt'}
                            className="px-2.5 py-1 text-xs font-medium text-[#00244F] border border-[#00244F]/30 rounded-lg hover:bg-[#00244F]/5 disabled:opacity-40"
                          >
                            {retranslatingId === item.id ? '…' : '↻ IA'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {sample.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-400">
                        Aucune traduction à revoir pour cette table / langue.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'glossary' && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <select
              value={glossaryLang}
              onChange={(e) => setGlossaryLang(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none"
            >
              {TRANSLATION_LANGS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
            </select>
            <p className="text-xs text-gray-500">
              Les entrées du glossaire sont injectées dans le prompt IA comme traductions imposées.
            </p>
          </div>

          {/* Ajout */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newTermFr}
              onChange={(e) => setNewTermFr(e.target.value)}
              placeholder="Terme français…"
              className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none"
            />
            <input
              type="text"
              value={newTerm}
              onChange={(e) => setNewTerm(e.target.value)}
              placeholder={`Traduction ${glossaryLang.toUpperCase()}…`}
              dir={glossaryLang === 'ar' ? 'rtl' : 'ltr'}
              className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none"
            />
            <input
              type="text"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Note (optionnel)…"
              className="w-56 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none"
            />
            <button
              onClick={addGlossaryEntry}
              disabled={!newTermFr.trim() || !newTerm.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg disabled:opacity-50"
            >
              Ajouter
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                  <th className="px-4 py-2.5 font-medium">Français</th>
                  <th className="px-4 py-2.5 font-medium">Traduction</th>
                  <th className="px-4 py-2.5 font-medium">Note</th>
                  <th className="px-4 py-2.5 font-medium w-20 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {glossary.map((g) => (
                  <tr key={g.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-gray-800">{g.termFr}</td>
                    <td className="px-4 py-2.5 text-gray-800" dir={g.lang === 'ar' ? 'rtl' : 'ltr'}>{g.term}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{g.note}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Supprimer « ${g.termFr} » ?`)) return;
                          await deleteGlossaryEntry(g.id);
                          await loadGlossary();
                        }}
                        title="Supprimer"
                        className="px-1.5 text-gray-400 hover:text-red-600"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
                {glossary.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">
                      Aucune entrée pour cette langue.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default TranslationReview;
